import React, { useEffect, useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

import WidgetConfig from "./components/section/WidgetConfig";
import {
  createComponent,
  updateComponent,
  uploadWidget,
} from "../../api/legacy/ComponentAPI";
import ProjectAPI from "../../api/ProjectAPI";
import { PLATFORM, SECTION } from "../../api/ProjectAPI.types";
import Breadcrumbs from "../../components/utils/Breadcrumbs";
import ContainerHeader from "../../components/utils/ContainerHeader";
import { COMPONENT_TYPES } from "../../constants/componentType";
import ItemStatus from "../../utils/ItemStatus";

import type { ChangeEvent, JSX } from "react";

import type {
  GetProjectPageComponentListResponse,
  GetProjectPageResponse,
  WidgetComponent,
} from "../../api/ProjectAPI.types";
import type { PlatformRoutesParams } from "../../components/routes/PlatformRoutes";
import type { AppState } from "../../reducers";

const containerHeaderLinks = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000097766-the-smart-components-editor-of-your-homepage",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000097766-l-editeur-des-smart-composants-de-votre-page-d-accueil",
};

const { PAGE } = SECTION;
const { WEB } = PLATFORM;

interface LocationState {
  next_rank?: number;
}

interface UploadWidgetResponse {
  data?: {
    uuid: string;
    url: string;
  };
}

export default function PlatformComponentWidget(): JSX.Element {
  const dispatch = useDispatch();
  const { projectSlug, platform, componentId, pageId } =
    useParams<PlatformRoutesParams>();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const queryClient = useQueryClient();

  const { WIDGET } = COMPONENT_TYPES;

  const { t } = useTranslation("editor-widget");

  const token = useSelector((state: AppState) => state.loginReducer.token);

  const isNew = componentId === "new";

  const { next_rank: nextRank } = location.state || {
    page: PAGE,
    platform: WEB,
  };

  const getComponentQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getComponent<WidgetComponent>,
    params: [{ projectSlug, componentId }],
    options: {
      enabled: !isNew,
    },
  });

  const { data: componentData } = useQuery(getComponentQuery);

  const getProjectPageComponentListQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPageComponentList,
    params: [{ pageId, platform, projectSlug }],
    options: {
      enabled: isNew,
    },
  });

  const { data: projectPageComponentListData } = useQuery({
    ...getProjectPageComponentListQuery,
    select: (data) => data.components,
  });

  const getProjectPageQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPage,
    params: [{ pageId, projectSlug }],
    options: {
      enabled: isNew,
    },
  });

  const { data: projectPageData } = useQuery(getProjectPageQuery);

  const [itemState, setItemState] = useState<WidgetComponent>({
    section: PAGE,
    type: WIDGET,
    storefront_project_page_uuid: pageId,
    platform: platform as WidgetComponent["platform"],
    label: t("newWidget"),
    enabled: true,
    rank:
      projectPageComponentListData?.length !== undefined
        ? projectPageComponentListData.length + 1
        : 0,
    uuid: "",
    dynamic_component: null,
    static_component: null,
    params: {
      width: "100%",
      height: "auto",
      enable_scroll: true,
      src_type: "html_template",
      external_url: "",
      template_url: "",
    },
  });

  const breadcrumbs = [
    {
      name: t("editor-menu:platform"),
      url: "/layout",
    },
    {
      name: t("editor-platform:title.layout"),
      url: "/layout",
    },
    {
      name: t(`editor-platform:page`),
      url: `/layout/pages/${pageId}/${platform}`,
    },
    {
      name: t("widget"),
    },
  ];

  const templateMode = itemState.params.src_type === "html_template";

  const handleSaveComponent = (
    component: WidgetComponent,
    isNewWidget: boolean,
  ): void => {
    const updatedComponent = { ...component };
    updatedComponent.rank = updatedComponent.rank || (nextRank ?? 0) + 1;

    if (updatedComponent.params.template_temp) {
      const { template_temp: templateTemp, ...paramsWithoutTemp } =
        updatedComponent.params;
      updatedComponent.params = paramsWithoutTemp;
    }

    if (isNewWidget) {
      dispatch(
        createComponent(
          updatedComponent,
          token,
          projectSlug,
          t,
          (response: WidgetComponent) => {
            const oldPath = history.location.pathname;
            const newPath = oldPath.replace(/\/(new)$/, `/${response.uuid}`);
            history.replace(newPath);
            queryClient.setQueryData(
              getProjectPageComponentListQuery.queryKey,
              (
                oldData?: GetProjectPageComponentListResponse,
              ): GetProjectPageComponentListResponse | undefined => {
                if (!oldData) return undefined;
                return {
                  ...oldData,
                  components: oldData.components.concat(response),
                };
              },
            );

            const hasPlatformInPage =
              projectPageData?.components_platform?.includes(platform);

            if (!hasPlatformInPage) {
              queryClient.setQueryData<GetProjectPageResponse>(
                getProjectPageQuery.queryKey,
                (oldData) => {
                  if (!oldData) return oldData;
                  return {
                    ...oldData,
                    components_platform: [
                      ...(oldData?.components_platform ?? []),
                      platform,
                    ],
                  };
                },
              );
            }
          },
        ),
      );
    } else {
      dispatch(
        updateComponent(
          updatedComponent,
          token,
          projectSlug,
          t,
          (response: WidgetComponent) => {
            queryClient.setQueryData(
              getProjectPageComponentListQuery.queryKey,
              (
                oldData?: GetProjectPageComponentListResponse,
              ): GetProjectPageComponentListResponse | undefined => {
                if (!oldData) return undefined;
                return {
                  ...oldData,
                  components: oldData.components.map((item) =>
                    item.uuid === response.uuid ? response : item,
                  ),
                };
              },
            );
            queryClient.setQueryData(getComponentQuery.queryKey, response);
          },
        ),
      );
    }
  };

  const handleSaveTemplate = (
    component: WidgetComponent,
    isNewWidget: boolean,
  ): void => {
    const blob = new Blob([component.params.template_temp ?? ""], {
      type: "text/html",
    });

    const formData = new FormData();
    if (component.uuid) {
      formData.append("storefront_project_component_uuid", component.uuid);
    }

    formData.append("file", blob);

    uploadWidget(
      formData,
      token,
      projectSlug,
      (request: UploadWidgetResponse) => {
        if (request?.data) {
          const updatedComponent = { ...component };
          updatedComponent.uuid = updatedComponent.uuid || request.data.uuid;
          updatedComponent.params.template_url = request.data.url;

          handleSaveComponent(updatedComponent, isNewWidget);
        }
      },
    );
  };

  useEffect(() => {
    if (!componentId) {
      history.push("/layout");
    }
  }, [componentId, history]);

  useEffect(() => {
    if (componentData && componentData.uuid !== itemState.uuid) {
      setItemState(componentData);
    }
  }, [componentId, itemState.uuid, componentData]);

  const handleChange = <K extends keyof WidgetComponent>(
    value: WidgetComponent[K],
    field: K,
  ): void => {
    setItemState((prevItem) => ({
      ...prevItem,
      [field]: value,
    }));
  };

  const handleChangeParams = <K extends keyof WidgetComponent["params"]>(
    value: WidgetComponent["params"][K],
    field: K,
  ): void => {
    setItemState((prevItem) => ({
      ...prevItem,
      params: {
        ...prevItem.params,
        [field]: value,
      },
    }));
  };

  const handleSave = (): void => {
    if (templateMode && itemState.params.template_temp) {
      handleSaveTemplate(itemState, isNew);
    } else {
      handleSaveComponent(itemState, isNew);
    }
  };

  return (
    <div className="size-full overflow-y-auto p-[3.75rem]">
      <Breadcrumbs items={breadcrumbs} />
      <ContainerHeader
        withBackBtn
        infoLinks={containerHeaderLinks}
        item={itemState}
        itemStatus={ItemStatus.getStatus(itemState)}
        onSave={handleSave}
        onChangeLabel={(e: ChangeEvent<HTMLInputElement>["target"]): void =>
          handleChange(e.value, "label")
        }
        onChangeState={(isEnabled: boolean): void =>
          handleChange(isEnabled, "enabled")
        }
      />

      <div className="editor-tile-container">
        <WidgetConfig component={itemState} onChange={handleChangeParams} />
      </div>
    </div>
  );
}
